import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, Phone } from "lucide-react";
import { tipPhases, packingTips, tipFaq } from "@/data/tips";
import { site } from "@/data/site";
import { Section, SectionHeading } from "@/components/site/Section";
import { CtaBand } from "@/components/site/CtaBand";
import { FaqBlock } from "@/components/site/FaqBlock";
import { ReviewBar } from "@/components/site/ReviewBar";
import { JsonLd } from "@/components/site/JsonLd";
import { meta, faqSchema } from "@/lib/schema";
import { overviewKeywords } from "@/lib/localKeywords";

const title = "Verhuistips & verhuischecklist stap voor stap | Vakkundig";
const description =
  "Het complete verhuisstappenplan van Vakkundig Verhuizingen & Transport: checklist van 8 weken vooraf tot na de verhuizing, inpaktips en antwoorden op de meest gestelde vragen.";

export const Route = createFileRoute("/verhuistips")({
  head: () => meta({
      title,
      description,
      path: "/verhuistips",
      image: "dozen",
      keywords: overviewKeywords(["verhuistips", "checklist verhuizen", "handig verhuizen", "verhuisdozen inpakken tips"]),
    }),
  component: TipsPage,
});

function TipsPage() {
  return (
    <>
      <JsonLd data={faqSchema(tipFaq)} />

      <Section tone="navy">
        <SectionHeading
          as="h1"
          eyebrow="Verhuistips & checklist"
          title="Het complete verhuisstappenplan van Vakkundig"
          intro="Wij verhuizen elke week huishoudens en kantoren door heel Nederland. Alles wat wij in de praktijk leren, staat hieronder: een checklist van acht weken vooraf tot de dag na de verhuizing, plus onze eigen inpaktips."
          inverted
        />
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/offerte"
            className="rounded-md bg-brand px-5 py-3 font-bold text-brand-foreground transition hover:bg-brand-dark"
          >
            Vraag direct je offerte aan
          </Link>
          <a
            href={site.phoneHref}
            className="inline-flex items-center gap-2 rounded-md border border-white/25 px-5 py-3 font-bold transition hover:border-brand hover:text-brand"
          >
            <Phone className="size-4" aria-hidden />
            {site.phone}
          </a>
        </div>
      </Section>

      <ReviewBar />

      <Section>
        <SectionHeading
          eyebrow="Stap voor stap"
          title="Verhuischecklist: van acht weken vooraf tot na de verhuizing"
          intro="Werk de lijst van boven naar beneden af. Twijfel je over een stap? Bel ons, dan denken onze planners met je mee."
        />
        <div className="mt-10 space-y-6">
          {tipPhases.map((phase, index) => (
            <article
              key={phase.period}
              className="rounded-2xl border border-border bg-background p-6 shadow-[var(--shadow-card)] sm:p-8"
            >
              <div className="flex flex-wrap items-center gap-3">
                <span className="grid size-9 shrink-0 place-items-center rounded-full bg-brand font-bold text-brand-foreground">
                  {index + 1}
                </span>
                <span className="rounded-full bg-muted px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-brand">
                  {phase.period}
                </span>
              </div>
              <h3 className="mt-4 font-display text-xl font-bold text-secondary sm:text-2xl">
                {phase.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{phase.intro}</p>
              <ul className="mt-5 space-y-3">
                {phase.items.map((item) => (
                  <li key={item} className="flex gap-3 text-sm leading-relaxed text-secondary">
                    <Check className="mt-0.5 size-4 shrink-0 text-brand" aria-hidden />
                    {item}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </Section>

      <Section tone="muted">
        <SectionHeading
          eyebrow="Inpakken"
          title="Onze inpaktips uit de praktijk"
          intro="Dit zijn de trucs waarmee onze verhuizers dagelijks werken. Neem ze over, of laat het inpakken van de grote items gewoon aan ons over."
        />
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {packingTips.map((tip) => (
            <div key={tip.title} className="rounded-xl border border-border bg-background p-6">
              <h3 className="font-display text-lg font-bold text-secondary">{tip.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{tip.text}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Veelgestelde vragen"
          title="Vragen over verhuizen, beantwoord door Vakkundig"
          intro="Staat jouw vraag er niet bij? Mail ons op info@vakkundig-verhuizingen-transport.nl of bel ons, zeven dagen per week."
        />
        <div className="mt-8">
          <FaqBlock items={tipFaq} />
        </div>
      </Section>

      <CtaBand title="Liever alles laten regelen door Vakkundig?" />
    </>
  );
}
